import { type ComponentType, lazy } from 'react';

import { PersistService } from './persist';

type Options = {
  retries?: number;
  interval?: number;
  exponentialBackOff?: boolean;
};

async function retry<T>(
  fn: () => Promise<T>,
  { retries = 3, interval = 500, exponentialBackOff = true }: Options = {},
) {
  return new Promise<T>((resolve, reject) => {
    const hasRefreshed = PersistService.get<boolean>('retry-lazy-refreshed') ?? false;

    fn()
      .then((component) => {
        PersistService.set('retry-lazy-refreshed', false);

        return resolve(component);
      })
      .catch(() => {
        setTimeout(() => {
          if (retries === 1 && !hasRefreshed) {
            PersistService.set('retry-lazy-refreshed', true);

            return window.location.reload();
          }

          retry(fn, { retries: retries - 1, interval: exponentialBackOff ? interval * 2 : interval }).then(
            resolve,
            reject,
          );
        }, interval);
      });
  });
}

export function createLazyImport<
  Props extends Record<string, unknown>,
  Module extends Record<Key, ComponentType<Props>> = Record<PropertyKey, ComponentType<Props>>,
  Key extends keyof Module = keyof Module,
>(loader: () => Promise<Module>, moduleName: Key) {
  return lazy(() => loader().then((m) => ({ default: m[moduleName] })));
}

export function createLazyPageImport<
  Props extends Record<string, unknown>,
  Module extends Record<Key, ComponentType<Props>> = Record<PropertyKey, ComponentType<Props>>,
  Key extends keyof Module = keyof Module,
>(loader: () => Promise<Module>, moduleName: Key) {
  return lazy(() => retry(() => loader().then((m) => ({ default: m[moduleName] }))));
}
